import { Form } from '@unform/web';
import styled from 'styled-components';

import { Select } from '../../../../components/Select';
import { colors, radius } from '../../../../styles/tokens';

export const Container = styled.div`
  padding: 40px;

  @media (max-width: 1199px) {
    padding: 20px;
  }
`;

export const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;

  span {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.15em;
  }

  span:first-child {
    color: ${colors.primary};
  }

  span:last-child {
    color: ${colors.onSurfaceVariant};
  }
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 24px;
  margin-bottom: 32px;

  @media (max-width: 1199px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

export const PageTitle = styled.h1`
  font-size: 40px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: -0.02em;
  color: ${colors.onSurface};
`;

export const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;
`;

export const Discard = styled.button`
  padding: 0 24px;
  height: 40px;

  background-color: transparent;
  border-radius: ${radius.sm};
  border: 1px solid ${colors.outlineVariant}4d;
  cursor: pointer;

  font-size: 12px;
  font-weight: 700;
  color: ${colors.onSurfaceVariant};
  text-transform: uppercase;
  letter-spacing: 0.08em;

  transition: color 0.2s, border-color 0.2s;

  &:hover {
    color: ${colors.primary};
    border-color: ${colors.primary};
  }
`;

export const SaveButton = styled.button`
  padding: 0 32px;
  height: 40px;

  background: linear-gradient(
    135deg,
    ${colors.primary} 0%,
    ${colors.primaryContainer} 100%
  );
  border: none;
  border-radius: ${radius.sm};
  cursor: pointer;

  font-size: 12px;
  font-weight: 700;
  color: ${colors.onPrimary};
  text-transform: uppercase;
  letter-spacing: 0.08em;

  transition: filter 0.2s, transform 0.1s;

  &:hover:not(:disabled) {
    filter: brightness(1.08);
  }

  &:active:not(:disabled) {
    transform: scale(0.96);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 32px;
  align-items: start;

  @media (max-width: 1199px) {
    grid-template-columns: 1fr;
  }
`;

export const FormContainer = styled(Form)`
  display: flex;
  flex-direction: column;
`;

export const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

export const MediaRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 639px) {
    grid-template-columns: 1fr;
  }
`;

export const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

export const Card = styled.div`
  background-color: ${colors.surfaceContainerLow};
  border-radius: ${radius.lg};
  padding: 32px;
`;

export const SidebarCard = styled(Card)`
  border-left: 2px solid ${colors.primary};
  background-color: ${colors.surfaceContainer};
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

export const CardTitle = styled.h3`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: ${colors.primary};
`;

export const SidebarTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 8px;

  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${colors.onSurface};
  margin-bottom: 24px;
`;

export const AutoSaveTag = styled.span`
  font-size: 10px;
  font-style: italic;
  color: ${colors.onSurfaceVariant};
`;

export const FieldGroup = styled.div`
  background-color: ${colors.surfaceContainerHighest}30;
  border-radius: ${radius.sm} ${radius.sm} 0 0;
  padding: 16px;

  border-bottom: 2px solid transparent;
  transition: border-color 0.2s;

  &:focus-within {
    border-color: ${colors.primary};
  }
`;

export const FieldLabel = styled.label`
  display: block;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${colors.onSurfaceVariant};
  margin-bottom: 8px;
`;

export const StaticValue = styled.p`
  font-size: 14px;
  font-weight: 700;
  color: ${colors.onSurface};
`;

export const TitleInput = styled.input`
  display: block;
  width: 100%;

  background: transparent;
  border: none;
  padding: 0;

  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${colors.onSurface};
  font-family: inherit;

  &:focus {
    outline: none;
  }
`;

export const DescriptionArea = styled.textarea`
  display: block;
  width: 100%;

  background: transparent;
  border: none;
  padding: 0;
  resize: none;

  font-size: 14px;
  line-height: 1.6;
  color: ${colors.onSurfaceVariant};
  font-family: inherit;

  &:focus {
    outline: none;
  }
`;

export const MuscleGroupSection = styled.div`
  &:not(:last-child) {
    margin-bottom: 24px;
    padding-bottom: 24px;
    border-bottom: 1px solid ${colors.outlineVariant}30;
  }
`;

export const MuscleGroupLabel = styled.p`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${colors.onSurfaceVariant};
  margin-bottom: 12px;
`;

export const ContainerCardsMuscleGroups = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

export const SelectField = styled(Select)``;

export const DifficultyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

export const DifficultyBars = styled.div`
  display: flex;
  gap: 4px;
`;

interface DifficultyBarProps {
  active?: boolean;
}

export const DifficultyBar = styled.div<DifficultyBarProps>`
  width: 24px;
  height: 4px;
  border-radius: ${radius.full};

  background-color: ${({ active }) =>
    active ? colors.primary : `${colors.outlineVariant}4d`};
`;

export const DifficultyLabel = styled.span`
  font-size: 10px;
  font-weight: 900;
  font-style: italic;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${colors.primary};
`;

export const UsageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

export const PulseDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: ${radius.full};
  background-color: ${colors.primary};

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.4;
    }
  }

  animation: pulse 2s ease-in-out infinite;
`;

export const UsageGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

export const UsageStat = styled.div`
  background-color: ${colors.surfaceContainerLow};
  border-radius: ${radius.DEFAULT};
  padding: 16px;

  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const UsageStatLabel = styled.p`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${colors.onSurfaceVariant};
`;

export const UsageStatValue = styled.p`
  font-size: 20px;
  font-weight: 900;
  color: ${colors.onSurface};
`;
